import { surfaceAuthCanvas, surfaceAuthCard } from '@/theme/designTokens'
import { SP_SIGNUP_NAVY, SP_SIGNUP_YELLOW, SP_SIGNUP_YELLOW_HOVER } from './signupShareTheme'
import {
  SURVEY_ENTRY_CARD_MAX_WIDTH_PX,
  SURVEY_ENTRY_CARD_PADDING_PX,
  SURVEY_ENTRY_STATS_TO_ACTIONS_GAP_PX,
  SURVEY_ENTRY_CARD_RADIUS_PX,
  SURVEY_ENTRY_CONTENT_MAX_WIDTH_PX,
  SURVEY_ENTRY_HEADER_BAND_HEIGHT_PX,
  SURVEY_ENTRY_LOGO_HEIGHT_PX,
  SURVEY_ENTRY_LOGO_WIDTH_PX,
  SURVEY_ENTRY_PAGE_SIDE_INSET_PX,
  SURVEY_ENTRY_SECTION_GAP_PX,
  SURVEY_ENTRY_START_BUTTON_WIDTH_PX,
} from './surveyEntryLayoutConstants'

/** Figma 136:3444 — top band */
export const surveyEntryHeaderNavy = SP_SIGNUP_NAVY
/** Figma primary text / headings */
export const surveyEntryHeadingColor = '#0f172a'
/** Figma secondary descriptions */
export const surveyEntryMutedColor = '#64748b'
/** Figma disabled / no-reward value */
export const surveyEntryDisabledColor = '#94a3b8'
/** Figma reward pill text */
export const surveyEntryRewardBlue = '#006192'
export const surveyEntryRewardAmountColor = '#04183f'

export const surveyEntryShellSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100dvh',
  maxHeight: '100dvh',
  overflow: 'hidden',
  position: 'relative',
  bgcolor: surfaceAuthCanvas,
  backgroundColor: surfaceAuthCanvas,
  boxSizing: 'border-box',
} as const

/** Figma 5074-14028 — fixed 260px navy band (must stay visible behind transparent main). */
export const surveyEntryHeaderBandSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: SURVEY_ENTRY_HEADER_BAND_HEIGHT_PX,
  bgcolor: surveyEntryHeaderNavy,
  backgroundColor: surveyEntryHeaderNavy,
  zIndex: 0,
  pointerEvents: 'none',
} as const

export const surveyEntryMainSx = {
  position: 'relative',
  zIndex: 1,
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  px: { xs: 2, md: `${SURVEY_ENTRY_PAGE_SIDE_INSET_PX}px` },
  pt: { xs: 2, md: 2 },
  pb: { xs: 2, md: 2 },
  overflowY: 'auto',
  boxSizing: 'border-box',
  backgroundColor: 'transparent',
} as const

/** Survey execution — questions scroll; copyright stays pinned like home. */
export const surveyExecutionShellSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100dvh',
  maxHeight: '100dvh',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  bgcolor: surfaceAuthCanvas,
  backgroundColor: surfaceAuthCanvas,
  boxSizing: 'border-box',
} as const

/** Figma 5074-13248 — navy bleed is flush to top (no main pt). */
export const surveyExecutionMainSx = {
  position: 'relative',
  zIndex: 1,
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  width: '100%',
  px: 0,
  pt: 0,
  pb: { xs: 3, md: 4 },
  overflowY: 'auto',
  boxSizing: 'border-box',
  backgroundColor: 'transparent',
} as const

export const surveyEntryPageRootSx = {
  width: '100%',
  maxWidth: SURVEY_ENTRY_CARD_MAX_WIDTH_PX,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  boxSizing: 'border-box',
} as const


/** Figma 5074-14035 — white card 1010×709 ref; height follows content (no forced empty space below CTA). */
export const surveyEntryCardSx = {
  position: 'relative',
  width: '100%',
  maxWidth: SURVEY_ENTRY_CARD_MAX_WIDTH_PX,
  minHeight: 'auto',
  height: 'auto',
  bgcolor: surfaceAuthCard,
  backgroundColor: surfaceAuthCard,
  border: '1px solid #bfc7d2',
  borderRadius: `${SURVEY_ENTRY_CARD_RADIUS_PX}px`,
  boxShadow: '0px 2px 15px -3px rgba(4, 24, 63, 0.08)',
  p: `${SURVEY_ENTRY_CARD_PADDING_PX}px`,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  overflow: { xs: 'visible', md: 'hidden' },
} as const

export const surveyEntryCardInnerSx = {
  width: '100%',
  maxWidth: SURVEY_ENTRY_CONTENT_MAX_WIDTH_PX,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: 0,
  boxSizing: 'border-box',
  '& > [data-survey-entry-logo-wrap]': {
    mb: `${SURVEY_ENTRY_SECTION_GAP_PX}px`,
  },
  '& > [data-survey-entry-card-body]': {
    mb: `${SURVEY_ENTRY_STATS_TO_ACTIONS_GAP_PX}px`,
  },
} as const

/** Content above Start Survey — 48px gap to actions row via card inner (Figma 5074-14071). */
export const surveyEntryCardBodySx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: `${SURVEY_ENTRY_SECTION_GAP_PX}px`,
  width: '100%',
  flexShrink: 0,
  boxSizing: 'border-box',
} as const

/** Figma 5074-14074 — centered at top of white card (5074-14035). */
export const surveyEntryLogoWrapSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  minHeight: SURVEY_ENTRY_LOGO_HEIGHT_PX,
  flexShrink: 0,
  boxSizing: 'border-box',
} as const

export const surveyEntryLogoImgSx = {
  display: 'block',
  width: SURVEY_ENTRY_LOGO_WIDTH_PX,
  height: SURVEY_ENTRY_LOGO_HEIGHT_PX,
  maxWidth: '100%',
  objectFit: 'contain',
} as const

export const surveyEntryHeaderBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '14.9px',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  width: '100%',
} as const

/** Figma 136:3457 — SURVEY AVAILABLE pill */
export const surveyEntryBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  bgcolor: '#fff8ec',
  backgroundColor: '#fff8ec',
  borderRadius: '9999px',
  px: '12px',
  py: '4px',
} as const

export const surveyEntryBadgeDotSx = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  bgcolor: '#f59e0b',
  backgroundColor: '#f59e0b',
  flexShrink: 0,
} as const

export const surveyEntryBadgeTextSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.4px',
  color: '#f59e0b',
  textTransform: 'uppercase',
} as const

/** Unavailable — status error, closed / paused / capacity, or non-active instrument (incl. ended TTL). */
export const surveyEntryBadgeUnavailableSx = {
  ...surveyEntryBadgeSx,
  bgcolor: '#fee2e2',
  backgroundColor: '#fee2e2',
} as const

export const surveyEntryBadgeDotUnavailableSx = {
  ...surveyEntryBadgeDotSx,
  bgcolor: '#dc2626',
  backgroundColor: '#dc2626',
} as const

export const surveyEntryBadgeTextUnavailableSx = {
  ...surveyEntryBadgeTextSx,
  color: '#dc2626',
} as const

/** Figma 5074-14045 — Head_bold_48 */
export const surveyEntryTitleSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '32px', sm: '40px', md: '48px' },
  lineHeight: { xs: '40px', sm: '48px', md: '57.6px' },
  letterSpacing: '-0.96px',
  color: surveyEntryHeadingColor,
} as const

/** Figma 5074-14047 — Head_semi_18 */
export const surveyEntryObjectiveSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  color: SP_SIGNUP_YELLOW,
  maxWidth: '672px',
} as const

export const surveyEntryStatsGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
  gap: '24px',
  width: '100%',
  alignSelf: 'stretch',
} as const

/** Figma 5074-14049 — stat card */
export const surveyEntryStatCardSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  minHeight: '112px',
  p: '25px',
  bgcolor: surfaceAuthCard,
  backgroundColor: surfaceAuthCard,
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
  boxSizing: 'border-box',
} as const

export const surveyEntryStatIconWrapSx = {
  width: 48,
  height: 48,
  borderRadius: '8px',
  bgcolor: '#fff8ec',
  backgroundColor: '#fff8ec',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const surveyEntryStatIconWrapRewardSx = surveyEntryStatIconWrapSx

/** Figma-exported vuesax SVGs — intrinsic fill/stroke; do not tint with `color`. */
export const surveyEntryStatIconImgSx = {
  width: 24,
  height: 24,
  display: 'block',
  flexShrink: 0,
} as const

export const surveyEntryStatLabelSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.8px',
  color: surveyEntryMutedColor,
  textTransform: 'uppercase',
} as const

export const surveyEntryStatValueSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '24px', sm: '30px' },
  lineHeight: { xs: '32px', sm: '36px' },
  color: surveyEntryHeadingColor,
} as const

export const surveyEntryStatValueDurationSx = {
  ...surveyEntryStatValueSx,
  fontSize: '20px',
  lineHeight: '28px',
} as const

export const surveyEntryStatValueMutedSx = {
  ...surveyEntryStatValueDurationSx,
  color: surveyEntryDisabledColor,
} as const

/** Figma 5074-14071 — shared actions row (alignment set per variant below). */
export const surveyEntryActionsRowSx = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '24px',
  width: '100%',
  flexShrink: 0,
} as const

/** Figma 5074-14025 — no reward: Start Survey centered in card. */
export const surveyEntryActionsRowCenteredSx = {
  ...surveyEntryActionsRowSx,
  justifyContent: 'center',
} as const

/** Figma 5076-7118 — with reward: Start Survey + pill left-aligned. */
export const surveyEntryActionsRowWithRewardSx = {
  ...surveyEntryActionsRowSx,
  justifyContent: 'flex-start',
} as const

export const surveyEntryStartButtonSx = {
  borderRadius: '8px',
  py: '16px',
  px: '24px',
  minHeight: 56,
  width: { xs: '100%', md: `${SURVEY_ENTRY_START_BUTTON_WIDTH_PX}px` },
  maxWidth: '100%',
  flexShrink: 0,
  alignSelf: 'center',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textTransform: 'none',
  boxShadow: 'none',
  bgcolor: SP_SIGNUP_YELLOW,
  backgroundColor: SP_SIGNUP_YELLOW,
  color: SP_SIGNUP_NAVY,
  '&:hover': {
    bgcolor: SP_SIGNUP_YELLOW_HOVER,
    backgroundColor: SP_SIGNUP_YELLOW_HOVER,
    color: SP_SIGNUP_NAVY,
  },
  '&.Mui-disabled': {
    bgcolor: SP_SIGNUP_YELLOW,
    backgroundColor: SP_SIGNUP_YELLOW,
    color: SP_SIGNUP_NAVY,
    opacity: 0.45,
  },
} as const

/** Figma 5076-7161 — reward pill beside Start Survey */
export const surveyEntryRewardPillSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: '0 1 auto',
  minHeight: '57px',
  px: '25px',
  py: '17px',
  borderRadius: '30px',
  border: '1px solid #cce5ff',
  bgcolor: 'rgba(204, 229, 255, 0.3)',
  backgroundColor: 'rgba(204, 229, 255, 0.3)',
  boxSizing: 'border-box',
} as const

export const surveyEntryRewardPillTextSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  color: surveyEntryRewardBlue,
} as const

export const surveyEntryRewardPillAmountSx = {
  fontWeight: 700,
  color: surveyEntryRewardAmountColor,
} as const

export const surveyEntryStatusAlertSx = {
  borderRadius: '8px',
  width: '100%',
  alignSelf: 'stretch',
  '& .MuiAlert-message': {
    fontSize: '14px',
    lineHeight: '20px',
  },
} as const

export const surveyEntryLoadingSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 320,
  width: '100%',
} as const
